import React from "react";
import { KioskLegacyRedirect, LEGACY_KIOSK_ROUTES, isKioskPath } from "./kioskRoutes";
import "../utils/default.css";
import Admin from "../Admin/Admin.jsx";
import RegisterForm from "../RegisterForm/RegisterForm.jsx";
import TopUsers from "../Admin/TopUsers.jsx";
import { ThemeProvider } from "@material-ui/core/styles";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { AnimationProvider } from "../Contexts/AnimationContext.jsx";
import { StatusProvider } from "../Contexts/StatusContext.jsx";
import StatusBanner from "../Components/StatusBanner.jsx";
import { KioskProvider } from "../Contexts/KioskContext.jsx";
import LoginCAS from "../Login/LoginCAS";
import LogoutCas from "../Login/LogoutCas.jsx";
import WelcomeNewUser from "../Login/WelcomeNewUser.jsx";
import { ChakraProvider, Flex } from "@chakra-ui/react";
import { chakraTheme, muiTheme } from "./theme.jsx";
import ToolSelection from "../User/ToolSelection.jsx";
import Profile from "../User/Profile.jsx";
import KioskLogin from "../Login/KioskLogin";
import InvalidScreen from "../Components/Invalid";
import ProtectedRoute from "../Components/ProtectedRoute";
import KioskLoadingAnimation from "../Components/KioskLoadingAnimation";
import BadgrCallback from "../User/BadgrCallback";
import Backend from "../utils/api";
import KioskConfirmation from "../Components/KioskConfirmation";
import useVersionCheck from "../hooks/useVersionCheck";
import KioskUsageConfirmation from "../Components/KioskUsageConfirmation";
import LabStatus from "../LabStatus/LabStatus.jsx"
import CustomReasons from "../Login/CustomReasons.jsx"
import DashboardPage from "../Dashboard/DashboardPage.jsx";
import { FeatureFlagsProvider, FRONTEND_V2_BETA } from "../Contexts/FeatureFlagsContext";
import LandingRedirect from "../Components/LandingRedirect";
// import BadgrCallback from "../User/BadgrCallback";

// Dev-only design-system smoke screen. Lazy-loaded and excluded entirely in
// production so the Chakra-v3 design system (+ @emotion, react-icons) never
// ships in the main bundle. See DsCheck.jsx JSDoc: "Delete once the dashboard ships."
const DsCheck =
  process.env.NODE_ENV !== "production"
    ? React.lazy(() => import("../DsCheck/DsCheck.jsx"))
    : null;

function VersionChecker() {
  useVersionCheck();
  return null;
}

const SESSION_CHECK_SKIP_PATHS = ["/kiosk", ...Object.keys(LEGACY_KIOSK_ROUTES), "/register", "/logout", "/invalid", "/topusers", "/oauth", "/caslogin", "/ds-check"];

class App extends React.Component {
  state = {
    user: {},
    lab: {},
    sessionStatus: SESSION_CHECK_SKIP_PATHS.some((p) => window.location.pathname.startsWith(p)) || window.location.pathname === "/"
      ? "unauthenticated"
      : "checking",
  };

  constructor(props) {
    super(props);
  }

  async componentDidMount() {
    // First try to get API key from query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const apiKey = urlParams.get('apiKey');
    
    if (apiKey) {
      Backend.setApiKey(apiKey);
    } else {
      // Try to get API key from localStorage
      const storedKey = localStorage.getItem('pinpoint_kiosk_key');
      if (storedKey) {
        Backend.setApiKey(storedKey);
      } else if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEV_API_KEY) {
        Backend.setApiKey(process.env.REACT_APP_DEV_API_KEY);
      } else {
        console.warn('No API key found in query parameters or localStorage');
      }
    }

    // Check for existing CAS session on non-kiosk routes
    const path = window.location.pathname;
    if (!SESSION_CHECK_SKIP_PATHS.some((p) => path.startsWith(p))) {
      Backend.getSessionUser()
        .then((res) => {
          if (res.status === 200 && res.user) {
            return Backend.getUser(res.user);
          }
          throw new Error("No active session");
        })
        .then((userRes) => {
          if (userRes.status === 200) {
            this.setUser(userRes);
          } else {
            localStorage.removeItem("user");
            this.setState({ user: {}, sessionStatus: "unauthenticated" });
          }
        })
        .catch(() => {
          localStorage.removeItem("user");
          this.setState({ user: {}, sessionStatus: "unauthenticated" });
        });
    }

    // Now proceed with lab determination after API key is set
    if (
      isKioskPath(window.location.pathname) ||
      window.location.pathname.startsWith("/lab_status")
    ) {
      this.determineLab();
    }
    window.onpopstate = this.backButton;
  }

  setLabError = (message) => {
    this.setState({ lab: { id: "Error", name: message } });
  };

  determineLab = async () => {
    var lab;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const labId = urlParams.get("lab") || urlParams.get("siteName");

      if (labId !== null) {
        const res = await Backend.getLabById({ id: labId, UID: 0 });
        if (res.status === 401 || res.status === 403) {
          this.setLabError("Kiosk is not authorized to access Pinpoint — the API key may be missing or expired. Contact eds@umd.edu.");
          return;
        }
        if (res.status !== 200) {
          this.setLabError("Couldn't load space data — check the space ID and contact eds@umd.edu if the issue persists.");
          return;
        }
        lab = res;
      } else {
        const cached = JSON.parse(localStorage.getItem("lab"));
        if (cached && cached.id !== "Error") {
          lab = cached;
          const params = new URLSearchParams(window.location.search);
          params.set("lab", lab.id);
          window.history.replaceState(window.history.state, "", `${window.location.pathname}?${params}${window.location.hash}`);
        }
      }
    } catch (err) {
      this.setLabError("Couldn't connect to Pinpoint — check the network connection and contact eds@umd.edu if the issue persists.");
      return;
    }

    if (!lab) {
      if (window.location.hostname !== "pinpoint.umd.edu") {
        lab = { id: "TL1", name: "Test Lab 1" };
      } else {
        this.setLabError("This kiosk hasn't been assigned a space yet — contact eds@umd.edu to complete setup.");
        return;
      }
    }

    this.setLab(lab);
  };

  login = (e) => {
    this.setUser(e);
  };

  logout = () => {
    if (this.state.user) {
      this.setState({
        user: {},
      });
    }
    window.location = "/";
  };

  setLab = (lab) => {
    localStorage.setItem("lab", JSON.stringify(lab));
    this.setState({ lab: lab });
  };

  setUser = (user) => {
    localStorage.setItem("user", JSON.stringify(user));
    this.setState({ user: user, sessionStatus: "authenticated" });
  }

  render() {
    const user = this.state.user;
    const lab = this.state.lab;

    return (
      <ChakraProvider theme={chakraTheme}>
      <VersionChecker />
      <StatusProvider>
        <AnimationProvider>
          <ThemeProvider theme={muiTheme}>
            <FeatureFlagsProvider user={user} sessionStatus={this.state.sessionStatus}>
            <Router>
              <StatusBanner />
              {/* Skip link for keyboard navigation */}
            <a 
              href="#main-content" 
              style={{
                position: 'absolute',
                left: '-9999px',
                zIndex: 999,
                padding: '1em',
                backgroundColor: '#000',
                color: '#fff',
                textDecoration: 'none'
              }}
              onFocus={(e) => {
                e.target.style.left = '0';
                e.target.style.top = '0';
              }}
              onBlur={(e) => {
                e.target.style.left = '-9999px';
              }}
            >
              Skip to main content
            </a>
            {this.state.sessionStatus === "checking" ? (
              <div style={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
                <KioskLoadingAnimation />
              </div>
            ) : (
            <Switch>
                <Route exact path={Object.keys(LEGACY_KIOSK_ROUTES)} component={KioskLegacyRedirect} />
                <Route exact path="/kiosk">
                  <KioskProvider>
                    <KioskLogin
                      onSubmit={(e) => {
                        this.setUser(e);
                      }}
                      currLab={lab}
                      setCurrLab={this.setLab}
                      routeFunc={(user_res, lab_permission_res) => {
                        if (user_res.status === 200) {
                          return "/kiosk/check-in/purpose";
                        } else {
                          return "/kiosk/register";
                        }
                      }}
                    />
                  </KioskProvider>
                </Route>
                <Route exact path="/"
                  render={() =>
                    user.UID ? (
                      <LandingRedirect />
                    ) : (
                      <LoginCAS onSubmit={(e) => this.setUser(e)} />
                    )
                  }
                />

                <Route exact path="/caslogin"
                  render={() =>
                    user.UID ? (
                      <LandingRedirect />
                    ) : (
                      <LoginCAS onSubmit={(e) => this.setUser(e)} />
                    )
                  }
                />

                <Route
                  path="/kiosk/check-in/tools"
                  render={(props) =>
                    user.UID === undefined ? (
                      <Redirect to={{ pathname: "/kiosk", search: props.location.search }} />
                    ) : (
                      <KioskProvider>
                        <ToolSelection user={user} lab={lab} />
                      </KioskProvider>
                    )
                  }
                />
                <Route
                  path="/invalid"
                  render={(props) => <InvalidScreen></InvalidScreen>}
                />
                <Route path="/oauth/*">
                  <BadgrCallback
                    user={ JSON.parse(localStorage.getItem("user")) }
                    onLogin={(e) => this.setUser(e)}
                  />
                </Route>
                <Route
                  path="/welcome"
                  render={(props) =>
                    user.UID === undefined ? (
                      <Redirect to="/" />
                    ) : (
                      <WelcomeNewUser />
                    )
                  }
                />
                <ProtectedRoute
                  path="/dashboard"
                  sessionStatus={this.state.sessionStatus}
                  user={user}
                  component={DashboardPage}
                  featureFlag={FRONTEND_V2_BETA}
                  flagOffRedirect="/profile"
                />
                <ProtectedRoute
                  path="/profile"
                  sessionStatus={this.state.sessionStatus}
                  user={user}
                  component={Profile}
                />
                <ProtectedRoute
                  path="/admin"
                  sessionStatus={this.state.sessionStatus}
                  user={user}
                  component={Admin}
                />
                <Route path="/logout">
                  <LogoutCas></LogoutCas>
                </Route>

                <Route path="/topusers" render={(props) => <TopUsers />} />
                <Route
                  path={["/register", "/kiosk/register"]}
                  render={(props) => {
                    const uid = user.UID ?? props.location?.state?.UID;
                    if (!uid) {
                      return <Redirect to={{ pathname: isKioskPath(props.location.pathname) ? "/kiosk" : "/", search: props.location.search }} />;
                    }
                    const hasLab = isKioskPath(props.location.pathname) && lab && lab.id;
                    return (
                      <RegisterForm
                        UID={uid}
                        routeFunc={(account_creation_res) => {
                          if (
                            account_creation_res.status === 200 ||
                            account_creation_res.status === 201
                          ) {
                            return hasLab ? "/kiosk/check-in/purpose" : "/welcome";
                          }
                          return hasLab ? "/kiosk" : "/";
                        }}
                        onSubmit={(e) => {
                          this.setUser(e);
                        }}
                      />
                    );
                  }}
                />
                <Route path="/kiosk/check-in/complete" render={() => (
                  <KioskProvider>
                    <KioskUsageConfirmation />
                  </KioskProvider>
                )} />
                <Route path="/kiosk/check-out/complete" render={() => (
                  <KioskProvider>
                    <KioskConfirmation
                      status="info"
                      heading="See you next time!"
                      subtitle="Thank you for using Pinpoint."
                      title="Thank You – Pinpoint"
                    />
                  </KioskProvider>
                )} />
                <Route exact path="/lab_status" render={() =>
                  this.state.sessionStatus === "checking" ? (
                    <Flex minH="100vh" align="center" justify="center">
                      <KioskLoadingAnimation />
                    </Flex>
                  ) : !user?.UID ? (
                    <Redirect to="/" />
                  ) : (
                    <LabStatus currLab={lab} />
                  )
                } />
                <Route exact path="/kiosk/check-in/purpose" render={(props) => user.UID === undefined
                  ? <Redirect to={{ pathname: "/kiosk", search: props.location.search }} />
                  : <KioskProvider><CustomReasons user={user} lab={lab} /></KioskProvider>} />
                {process.env.NODE_ENV !== "production" && (
                  <Route
                    path="/ds-check"
                    render={() => (
                      <React.Suspense fallback={null}>
                        <DsCheck />
                      </React.Suspense>
                    )}
                  />
                )}

              </Switch>
            )}
            </Router>
            </FeatureFlagsProvider>
          </ThemeProvider>
        </AnimationProvider>
      </StatusProvider>
      </ChakraProvider>
    );
  }
}

export default App;
